import { useState } from "react";
import { X, Calendar, Loader2 } from "lucide-react";

interface CreateJournalModalProps {
  onClose: () => void;
  onCreate: (date: string) => Promise<void>;
}

/**
 * Modal for creating a new daily journal
 *
 * @param onClose - Callback to close the modal
 * @param onCreate - Callback to create a journal for the selected date
 * @returns Modal dialog for journal creation
 */
export function CreateJournalModal({
  onClose,
  onCreate,
}: CreateJournalModalProps) {
  const [date, setDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  });
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Handle journal creation
   */
  const handleCreate = async () => {
    setIsLoading(true);
    try {
      await onCreate(date);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md p-6 animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-display font-semibold text-primary-950">
            Create Journal
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Date selection */}
        <div className="mb-6">
          <label
            htmlFor="journal-date"
            className="block text-sm font-medium text-slate-700 mb-2"
          >
            Select Date
          </label>
          <div className="relative">
            <Calendar
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              size={20}
            />
            <input
              id="journal-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              max={new Date().toISOString().split("T")[0]}
              className="w-full pl-10 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <p className="text-sm text-slate-500 mt-2">
            A journal will be generated from all conversations on this date.
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 border rounded-xl font-medium text-slate-700 hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={isLoading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 text-white rounded-xl font-medium hover:bg-primary-700 transition-colors disabled:opacity-50"
          >
            {isLoading ? (
              <>
                <Loader2 className="animate-spin" size={18} />
                Generating...
              </>
            ) : (
              "Generate Journal"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

